import React from "react";
import { Avatar, Box, Typography } from "@mui/material";

const EXTRA_POWER_URL =
  "https://www.supergas-power.co.il/%D7%94%D7%98%D7%91%D7%95%D7%AA/";

const sections = [
  {
    key: "lpg",
    label: "שירות גז",
    icon: "gas-tank.svg",
  },
  {
    key: "power",
    label: "שירות חשמל",
    icon: "electricity.svg",
  },
] as const;

export type SectionKey = (typeof sections)[number]["key"] | "extraPower";

interface SectionSelectorProps {
  activeSection: SectionKey | null;
  onSectionClick: (section: SectionKey) => void;
}

export default function SectionSelector({
  activeSection,
  onSectionClick,
}: SectionSelectorProps) {
  return (
    <div
      className="row"
      style={{
        textAlign: "center",
        marginBottom: 10,
      }}
    >
      <Typography
        variant="h5"
        sx={{
          mb: 5,
          color: "primary.main",
          letterSpacing: 1,
        }}
      >
        <strong
          style={{
            fontWeight: 900,
            color: "black",
          }}
        >
          ברוכים הבאים 💚
        </strong>
        <br />
        פשוט בוחרים בתחום השירות הרצוי 👇🏿 ומתחילים!
        <br />
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        {sections.map(({ key, label, icon }) => {
          const isActive = activeSection === key;

          return (
            <Box
              key={key}
              role="button"
              tabIndex={0}
              onClick={() => onSectionClick(key)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSectionClick(key);
                }
              }}
              sx={{
                display: "flex",
                flexDirection: "row-reverse",
                alignItems: "center",
                justifyContent: "space-between",
                width: 230,
                bgcolor: "#f6f6f6",
                borderRadius: 4,
                px: 3,
                py: 2,
                cursor: "pointer",
                boxShadow: isActive ? 4 : 2,
                border: "2px solid #01F156",
                transition: "all 0.2s ease-in-out",

                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 4,
                },

                "&:focus-visible": {
                  outline: "3px solid rgba(1, 241, 86, 0.35)",
                  outlineOffset: 3,
                },
              }}
            >
              <Typography
                variant="h5"
                fontWeight={isActive ? 800 : 700}
                color={isActive ? "primary" : "text.primary"}
                textAlign="right"
              >
                {label}
              </Typography>

              <Avatar
                sx={{
                  bgcolor: isActive ? "primary.main" : "grey.100",
                  width: 56,
                  height: 56,
                }}
              >
                <img
                  src={`/svg/${icon}`}
                  alt=""
                  style={{
                    width: "1.8em",
                    height: "1.8em",
                  }}
                />
              </Avatar>
            </Box>
          );
        })}

        {/* Extra Power external-link button */}
        <Box
          component="a"
          href={EXTRA_POWER_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="מעבר לארנק ההטבות EXTRA POWER"
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: 230,
            minHeight: 86,
            px: 3,
            py: 1.4,
            bgcolor: "#00F51A",
            borderRadius: "16px",
            cursor: "pointer",
            overflow: "visible",
            boxShadow: 2,
            border: "2px solid #00F51A",
            textDecoration: "none",
            transition: "all 0.2s ease-in-out",

            "&:hover": {
              bgcolor: "#00F51A",
              textDecoration: "none",
              transform: "translateY(-2px)",
              boxShadow: 4,
            },

            "&:focus-visible": {
              outline: "3px solid rgba(0, 0, 0, 0.25)",
              outlineOffset: 3,
            },
          }}
        >
          {/* NEW badge */}
          <Box
            sx={{
              position: "absolute",
              top: -13,

              // Physical left side when the page direction is RTL.
              insetInlineEnd: -13,

              width: 42,
              height: 42,
              zIndex: 2,
              pointerEvents: "none",
            }}
          >
            <Box
              component="img"
              src="/NEW-icon.png"
              alt=""
              sx={{
                display: "block",
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />

            <Typography
              component="span"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                color: "#00F51A",
                fontSize: 11,
                fontWeight: 900,
                lineHeight: 1,
                letterSpacing: 0.4,
              }}
            ></Typography>
          </Box>

          <Typography
            component="div"
            sx={{
              mb: 0.6,
              color: "#000",
              fontSize: 18,
              fontWeight: 800,
              lineHeight: 1.15,
              direction: "rtl",
            }}
          >
            ארנק ההטבות
          </Typography>

          <Box
            component="img"
            src="/logo-extra.png"
            alt="EXTRA POWER"
            sx={{
              display: "block",
              width: 165,
              maxWidth: "88%",
              height: "auto",
              filter: "brightness(0)",
            }}
          />
        </Box>
      </Box>
    </div>
  );
}
